import React, { useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

// Convert BLK/2026/N/0001/P1/G1 → N-BOX/0001/P1/G1
const formatCode = (code) => {
  if(!code) return code;
  const parts = code.split('/');
  if(parts.length >= 4 && parts[2].length === 1) {
    const dir = parts[2];
    const serial = parts[3];
    const rest = parts.slice(4).join('/');
    return rest ? `${dir}-BOX/${serial}/${rest}` : `${dir}-BOX/${serial}`;
  }
  return code;
};

const today = new Date().toISOString().split('T')[0];
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
const fmt = d => { try{ return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}); }catch{ return d; }};

const SBadge = ({ status }) => {
  const map = {
    RECEIVED:   ['#E6F1FB','#185FA5'],
    IN_PROCESS: ['#FAEEDA','#854F0B'],
    COMPLETED:  ['#EAF3DE','#3B6D11'],
    REJECTED:   ['#FDE8E8','#C0392B'],
    AVAILABLE:  ['#EAF3DE','#3B6D11'],
    IN_GANGSAW: ['#FAEEDA','#854F0B'],
    PASSED:     ['#EAF3DE','#3B6D11'],
    FAILED:     ['#FDE8E8','#C0392B'],
    IN_PROCESS_SLAB:['#FAEEDA','#854F0B'],
    QC_PENDING: ['#F0E6FB','#6B2FA0'],
  };
  const [bg,color] = map[status]||['#f0f0f0','#666'];
  return <span style={{ fontSize:11, padding:'2px 10px', borderRadius:20, fontWeight:500, background:bg, color }}>{status?.replace(/_/g,' ')||'—'}</span>;
};

export default function ProductionReportPage() {
  const [filters, setFilters] = useState({ from: firstOfMonth, to: today });
  const [blocks, setBlocks]   = useState([]);
  const [parts, setParts]     = useState([]);
  const [slabs, setSlabs]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  const generate = async () => {
    setLoading(true);
    try {
      const [br, pr, sr] = await Promise.all([
        api.get(`/production/blocks/?page_size=500&received_date_after=${filters.from}&received_date_before=${filters.to}`),
        api.get(`/production/parts/?page_size=500&created_date_after=${filters.from}&created_date_before=${filters.to}`),
        api.get(`/production/slabs/?page_size=500&created_date_after=${filters.from}&created_date_before=${filters.to}`),
      ]);
      setBlocks(br.data.results||[]);
      setParts(pr.data.results||[]);
      setSlabs(sr.data.results||[]);
      setGenerated(true);
    } finally { setLoading(false); }
  };

  const completedBlocks = blocks.filter(b => b.status==='COMPLETED');
  const completedParts  = parts.filter(p => p.status==='IN_GANGSAW'||p.status==='COMPLETED');
  const passedSlabs     = slabs.filter(s => s.status==='PASSED');

  const blockStats = {
    total: blocks.length,
    received:   blocks.filter(b=>b.status==='RECEIVED').length,
    inProcess:  blocks.filter(b=>b.status==='IN_PROCESS').length,
    completed:  blocks.filter(b=>b.status==='COMPLETED').length,
    rejected:   blocks.filter(b=>b.status==='REJECTED').length,
  };
  const partStats = {
    total:      parts.length,
    available:  parts.filter(p=>p.status==='AVAILABLE').length,
    inGangsaw:  parts.filter(p=>p.status==='IN_GANGSAW').length,
    completed:  parts.filter(p=>p.status==='COMPLETED').length,
  };
  const slabStats = {
    total:      slabs.length,
    passed:     slabs.filter(s=>s.status==='PASSED').length,
    failed:     slabs.filter(s=>s.status==='FAILED').length,
    inProcess:  slabs.filter(s=>s.status==='IN_PROCESS').length,
    qcPending:  slabs.filter(s=>s.status==='QC_PENDING').length,
  };

  const exportCSV = () => {
    let rows, data, filename;
    if(activeTab==='blocks'){
      rows=[['Block Number','Color','Status','L(cm)','W(cm)','H(cm)','Volume(cm³)','Parts','Received']];
      data=blocks.map(b=>[formatCode(b.block_number),b.color,b.status,b.length,b.width,b.height||'—',b.volume_cubic_cm,b.part_count,b.received_date]);
      filename='production_blocks.csv';
    } else if(activeTab==='parts'){
      rows=[['Part Code','Part #','Status','L(cm)','W(cm)','H(cm)','Slabs','Quality']];
      data=parts.map(p=>[formatCode(p.part_code),p.part_number,p.status,p.length,p.width,p.height||'—',p.slab_count,p.quality_grade]);
      filename='production_parts.csv';
    } else {
      rows=[['Slab Code','Slab #','Status','Finish','Area(sqft)','Quality','Defects','Price/sqft']];
      data=slabs.map(s=>[formatCode(s.slab_code),s.slab_number,s.status,s.finish,s.area_sqft,s.quality_grade,s.has_defects?'Yes':'No',s.price_per_sqft||'—']);
      filename='production_slabs.csv';
    }
    const csv=[...rows,...data].map(r=>r.map(v=>`"${v}"`).join(',')).join('\n');
    const blob=new Blob([csv],{type:'text/csv'});
    const a=document.createElement('a');
    a.href=URL.createObjectURL(blob);
    a.download=filename; a.click();
  };

  return (
    <Layout title="Production Report" subtitle="Completion summary + current status">
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:14 }}>Date Range</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'end' }}>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">From Date</label>
            <input className="form-input" type="date" value={filters.from} onChange={e=>setF('from',e.target.value)} max={today}/>
          </div>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">To Date</label>
            <input className="form-input" type="date" value={filters.to} onChange={e=>setF('to',e.target.value)} max={today}/>
          </div>
          <div className="form-group" style={{ flex:'0 0 auto' }}>
            <label className="form-label" style={{ opacity:0 }}>x</label>
            <button className="btn btn-primary" onClick={generate} disabled={loading}>{loading?'Loading...':'Generate'}</button>
          </div>
        </div>
      </div>

      {generated && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
            <div>
              <div style={{ fontSize:16, fontWeight:600, color:'var(--text-primary)' }}>Production Report</div>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{fmt(filters.from)} to {fmt(filters.to)}</div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={()=>window.print()}>🖨 Print</button>
              <button className="btn btn-secondary btn-sm" onClick={exportCSV}>📊 Export</button>
            </div>
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>
              Production Completed — {fmt(filters.from)} to {fmt(filters.to)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16 }}>
              <div style={{ background:'var(--bg-primary)', borderRadius:'var(--radius-sm)', padding:16, textAlign:'center' }}>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:36, fontWeight:500, color:'var(--brand)' }}>{blockStats.completed}</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:4 }}>Blocks Completed</div>
                <div style={{ marginTop:10 }}>
                  {completedBlocks.slice(0,5).map((b,i)=>(
                    <div key={i} style={{ fontSize:11, color:'var(--text-muted)', padding:'2px 0' }}>{formatCode(b.block_number)}</div>
                  ))}
                  {completedBlocks.length>5&&<div style={{ fontSize:11, color:'var(--text-muted)' }}>+{completedBlocks.length-5} more</div>}
                </div>
              </div>
              <div style={{ background:'var(--bg-primary)', borderRadius:'var(--radius-sm)', padding:16, textAlign:'center' }}>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:36, fontWeight:500, color:'var(--blue)' }}>{completedParts.length}</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:4 }}>Parts Processed</div>
                <div style={{ marginTop:10 }}>
                  {completedParts.slice(0,5).map((p,i)=>(
                    <div key={i} style={{ fontSize:11, color:'var(--text-muted)', padding:'2px 0' }}>{formatCode(p.part_code)}</div>
                  ))}
                  {completedParts.length>5&&<div style={{ fontSize:11, color:'var(--text-muted)' }}>+{completedParts.length-5} more</div>}
                </div>
              </div>
              <div style={{ background:'var(--bg-primary)', borderRadius:'var(--radius-sm)', padding:16, textAlign:'center' }}>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:36, fontWeight:500, color:'var(--green)' }}>{passedSlabs.length}</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:4 }}>Slabs Passed QC</div>
                <div style={{ marginTop:8 }}>
                  <div style={{ fontSize:12, color:'var(--text-muted)' }}>
                    Total area: <span style={{ fontFamily:'DM Mono,monospace', color:'var(--green)', fontWeight:500 }}>
                      {passedSlabs.reduce((s,sl)=>s+(parseFloat(sl.area_sqft)||0),0).toFixed(2)} sqft
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{slabStats.failed} failed QC</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>
              Current Status Snapshot
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text-primary)', marginBottom:10, padding:'6px 10px', background:'var(--brand-dim)', borderRadius:'var(--radius-sm)' }}>📦 BLOCKS — {blockStats.total} total</div>
                {[['Received (New)',blockStats.received,'var(--blue)'],['In Process',blockStats.inProcess,'var(--amber)'],['Completed',blockStats.completed,'var(--green)'],['Rejected',blockStats.rejected,'var(--red)']].map(([label,val,color])=>(
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', borderBottom:'1px solid var(--border-light)', alignItems:'center' }}>
                    <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontFamily:'DM Mono,monospace', fontSize:16, fontWeight:500, color }}>{val}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text-primary)', marginBottom:10, padding:'6px 10px', background:'var(--blue-bg)', borderRadius:'var(--radius-sm)' }}>🪨 PARTS — {partStats.total} total</div>
                {[['Available',partStats.available,'var(--green)'],['In Gangsaw',partStats.inGangsaw,'var(--amber)'],['Completed',partStats.completed,'var(--brand)']].map(([label,val,color])=>(
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', borderBottom:'1px solid var(--border-light)', alignItems:'center' }}>
                    <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontFamily:'DM Mono,monospace', fontSize:16, fontWeight:500, color }}>{val}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text-primary)', marginBottom:10, padding:'6px 10px', background:'#EAF3DE', borderRadius:'var(--radius-sm)' }}>💎 SLABS — {slabStats.total} total</div>
                {[['Passed QC',slabStats.passed,'var(--green)'],['In Process',slabStats.inProcess,'var(--blue)'],['QC Pending',slabStats.qcPending,'var(--amber)'],['Failed QC',slabStats.failed,'var(--red)']].map(([label,val,color])=>(
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', borderBottom:'1px solid var(--border-light)', alignItems:'center' }}>
                    <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontFamily:'DM Mono,monospace', fontSize:16, fontWeight:500, color }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display:'flex', gap:0, marginBottom:16, border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflowX:'auto', maxWidth:'100%' }}>
            {[['overview','Overview'],['blocks',`Blocks (${blocks.length})`],['parts',`Parts (${parts.length})`],['slabs',`Slabs (${slabs.length})`]].map(([tab,label])=>(
              <button key={tab} onClick={()=>setActiveTab(tab)} style={{ padding:'8px 18px', fontSize:12, fontWeight:500, background:activeTab===tab?'var(--brand)':'var(--white)', color:activeTab===tab?'#fff':'var(--text-secondary)', border:'none', cursor:'pointer', borderRight:'1px solid var(--border)' }}>
                {label}
              </button>
            ))}
          </div>

          {activeTab==='overview' && (
            <div style={{ color:'var(--text-muted)', fontSize:13, padding:'10px 0' }}>Select a tab above to view detailed data.</div>
          )}

          {activeTab==='blocks' && (
            <div className="card">
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead><tr><th>Block Number</th><th>Color</th><th>Status</th><th>L × W × H (cm)</th><th>Volume (cm³)</th><th>Parts</th><th>Grade</th><th>Received</th></tr></thead>
                  <tbody>
                    {blocks.map((b,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:600, color:'var(--brand)' }}>{formatCode(b.block_number)}</td>
                        <td style={{ fontSize:12 }}>{b.color}</td>
                        <td><SBadge status={b.status}/></td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{b.length} × {b.width} × {b.height||'—'}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{b.volume_cubic_cm?.toLocaleString('en-IN')||'—'}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center' }}>{b.part_count||0}</td>
                        <td style={{ fontSize:12 }}>Grade {b.quality_grade}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{b.received_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab==='parts' && (
            <div className="card">
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead><tr><th>Part Code</th><th>Part #</th><th>Status</th><th>L × W × H (cm)</th><th>Slabs</th><th>Grade</th><th>Created</th></tr></thead>
                  <tbody>
                    {parts.map((p,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:600, color:'var(--brand)', fontSize:12 }}>{formatCode(p.part_code)}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700 }}>{p.part_number}</td>
                        <td><SBadge status={p.status}/></td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{p.length} × {p.width} × {p.height||'—'}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center' }}>{p.slab_count||0}</td>
                        <td style={{ fontSize:12 }}>Grade {p.quality_grade}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{p.created_date||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab==='slabs' && (
            <div className="card">
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead><tr><th>Slab Code</th><th>Slab #</th><th>Status</th><th>Finish</th><th>Area (sqft)</th><th>Grade</th><th>Defects</th><th>Price/sqft</th><th>Created</th></tr></thead>
                  <tbody>
                    {slabs.map((s,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:600, color:'var(--brand)', fontSize:12 }}>{formatCode(s.slab_code)}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700 }}>{s.slab_number}</td>
                        <td><SBadge status={s.status}/></td>
                        <td style={{ fontSize:12 }}>{s.finish||'—'}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', color:'var(--blue)', fontWeight:500 }}>{s.area_sqft?.toFixed(2)||'—'}</td>
                        <td style={{ fontSize:12 }}>Grade {s.quality_grade}</td>
                        <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:s.has_defects?'#FDE8E8':'#EAF3DE', color:s.has_defects?'#C0392B':'#3B6D11', fontWeight:500 }}>{s.has_defects?'Yes':'No'}</span></td>
                        <td style={{ fontFamily:'DM Mono,monospace', color:'var(--green)', fontWeight:500 }}>{s.price_per_sqft?`₹${s.price_per_sqft}`:'—'}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{s.created_date||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}